import { __rest } from "tslib";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { autoUpdate, flip, offset, useFloating } from '@floating-ui/react';
import { Popover, PopoverBackdrop, PopoverPanel } from '@headlessui/react';
import { forwardRef, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { LocaleContext, isRtl } from '../../../i18n/src';
import { Icon } from '../../../icon/src';
import { Input } from '../../../input/src';
import { DateCalendar } from './calendar/calendar_date';
import { useDatePicker } from './use_date_picker';
const PLACEMENT_MAP_LTR = {
    'bottom': 'bottom-start',
    'bottom-end': 'bottom-end',
    'top': 'top-start',
    'top-end': 'top-end',
};
const PLACEMENT_MAP_RTL = {
    'bottom': 'bottom-end',
    'bottom-end': 'bottom-start',
    'top': 'top-end',
    'top-end': 'top-start',
};
export const DatePicker = forwardRef(function DatePicker(props, ref) {
    var _a;
    const { description, disabled, errorMessage, hasError, id, label, onChange, required, hideRequiredIndicator, value, width = 'base', position = 'bottom', containLabels, portalContainer } = props, calendarProps = __rest(props, ["description", "disabled", "errorMessage", "hasError", "id", "label", "onChange", "required", "hideRequiredIndicator", "value", "width", "position", "containLabels", "portalContainer"]);
    const { inputValue, inputOnChange, calendarValue, calendarOnChange, error: internalError } = useDatePicker({ value });
    const { refs, floatingStyles } = useFloating({
        strategy: 'fixed',
        placement: isRtl(useContext(LocaleContext)) ? PLACEMENT_MAP_RTL[position] : PLACEMENT_MAP_LTR[position],
        whileElementsMounted: autoUpdate,
        middleware: [offset(12), flip({ crossAxis: false })],
    });
    // Notify parent component of changes to the calendar date
    useEffect(() => {
        onChange === null || onChange === void 0 ? void 0 : onChange(calendarValue);
    }, [calendarValue]);
    const [open, setOpen] = useState(false);
    const onEsc = (event) => {
        if (event.code === 'Escape') {
            setOpen(false);
        }
    };
    const calendarPortal = open &&
        createPortal(_jsxs(_Fragment, { children: [_jsx(PopoverBackdrop, { "data-testid": 'calendar-overlay', className: 'nucleus-fixed nucleus-inset-0 nucleus-z-20', onClick: () => setOpen(false), static: true }), _jsx(PopoverPanel, { ref: refs.setFloating, role: 'dialog', className: 'nucleus-z-50 nucleus-box-border nucleus-w-80 nucleus-border-solid nucleus-shadow-lg nucleus-rounded-md nucleus-border-1 nucleus-border-neutral-300 nucleus-p-8 nucleus-bg-white', onKeyDown: onEsc, static: true, style: floatingStyles, children: _jsx(DateCalendar, Object.assign({}, calendarProps, { required: required, value: calendarValue, onChange: calendarOnChange })) })] }), portalContainer !== null && portalContainer !== void 0 ? portalContainer : document.body);
    return (_jsx(Popover, { className: 'nucleus-relative nucleus-inline-block', ref: refs.setReference, children: _jsxs(_Fragment, { children: [_jsx(Input, { ref: ref, id: id, label: label, description: description, required: required, hideRequiredIndicator: hideRequiredIndicator, disabled: disabled, value: inputValue, onChange: inputOnChange, onFocus: () => {
                        setOpen(true);
                    }, onKeyDown: onEsc, endAdornment: _jsx(Icon, { name: 'date', "aria-hidden": true }), hasError: !!internalError || hasError, errorMessage: (_a = internalError === null || internalError === void 0 ? void 0 : internalError.message) !== null && _a !== void 0 ? _a : errorMessage, width: width, containLabels: containLabels }), calendarPortal] }) }));
});
//# sourceMappingURL=date_picker.js.map